import { useQuery } from '@tanstack/react-query';

import { readWorkflowLimits } from '@/lib/api/generated/api';
import type { WorkflowLimitRow } from '@/lib/api/generated/model';
import { UsageError, isForbidden } from '@/features/workspace-usage';

import { organizationKeys } from '../queryKeys';

/**
 * Every workflow that has a spend limit, with what it has spent (spec 080 US1).
 *
 * A second read rather than a field on the organization summary, because it answers a
 * different question about different subjects: that response is every workspace, this is
 * the handful of workflows somebody deliberately capped. Limits are opt-in, so an empty
 * list is the common case and means nobody has capped anything — not that the question
 * went unasked.
 *
 * Shares `UsageError` and the no-polling rule with `useOrganizationUsage` beside it. Two
 * reads on one page explaining the same refusal differently is how a reader learns to
 * distrust both.
 */
export function useWorkflowLimits(period?: string) {
  return useQuery<WorkflowLimitRow[]>({
    queryKey: organizationKeys.workflowLimits(period),
    queryFn: async ({ signal }) => {
      const response = await readWorkflowLimits(
        period === undefined ? {} : { period },
        { signal },
      );
      if (response.status === 200) return response.data;
      if (response.status === 403) {
        throw new UsageError(
          'forbidden',
          'Only an organization administrator can see workflow spend limits.',
        );
      }
      throw new UsageError(
        'unavailable',
        'Could not read workflow spend limits.',
      );
    },
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => !isForbidden(error) && failureCount < 2,
  });
}
